import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import moment from "moment";
import "./Package.css";
import "./PackageForm.css";
import hotelSearchData from "../../Data/HotelCities.json";


const ChangeHotelModal = ({ show, onHide, hotels, isLoading, onSelect, currentlySelectedHotel }) => {
    const savedHotelName = currentlySelectedHotel?.hotelName;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered dialogClassName="package-hotel-modal">
            <Modal.Header closeButton>
                <Modal.Title><i className="ri-hotel-line me-2"></i>Change Hotel</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="list-group" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {isLoading && (
                        <div className="text-center p-5">
                            <Spinner animation="border" />
                            <p className="mt-2 mb-0">Searching for alternative hotels...</p>
                        </div>
                    )}
                    {!isLoading && hotels && hotels.length > 0 ? (
                        hotels.slice(0, 50).map((hotel) => {
                            const isSelected = savedHotelName === hotel.hotelName;
                            return (
                                // This is the main flex container
                                <div key={hotel.hotelId} className="list-group-item packagehotelmodel-card-item">
                                    {/* Column 1: Image */}
                                    <div className="packagehotelmodel-card-image">
                                        <img src={hotel.mainImage || "/assets/images/Default_Hotel.jpg"} alt={hotel.hotelName} />
                                    </div>

                                    {/* Column 2: Details */}
                                    <div className="packagehotelmodel-card-details">
                                        <h5 className="packagehotelmodel-name mb-1">{hotel.hotelName}</h5>
                                        <div className="packagehotelmodel-rating mb-2">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <i key={i} className={`ri-star-${i < hotel.rating ? 'fill' : 'line'}`}></i>
                                            ))}
                                        </div>
                                        <p className="packagehotelmodel-address text-muted mb-0">
                                            <i className="ri-map-pin-line"></i> {hotel.address || hotel.cityName}
                                        </p>
                                    </div>

                                    {/* Column 3: Price & Action */}
                                    <div className="packagehotelmodel-card-price-action">
                                        <div className="price">INR {hotel.totalSalePrice?.toFixed(2)}</div>
                                        <p className="price-note text-muted">per stay</p>
                                        <Button
                                            variant={isSelected ? "success" : "primary"}
                                            size="sm"
                                            onClick={() => onSelect(hotel)}
                                            className="mt-2 w-100" // Make button full-width of its column
                                        >
                                            {isSelected ? "SELECTED" : "SELECT"}
                                        </Button>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        !isLoading && (
                            <div className="text-center p-4">
                                <h5>No alternative hotels found.</h5>
                                <p className="text-muted mb-0">Please try again, or no other options are available for these dates.</p>
                            </div>
                        )
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

// --- Main Component ---
const PackageHotelBooking = ({ packageId, packageDetails }) => {
    const initialFieldValues = {
        packageHotelBookingId: "00000000-0000-0000-0000-000000000000",
        packageId: packageId || "00000000-0000-0000-0000-000000000000",
        checkInDate: "", checkOutDate: "", city: "", nights: 0, hotelName: "", roomType: "", numberOfRooms: 1, price: 0,
    };

    const { user } = useAuth();
    const [lists, setLists] = useState([]);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});
    const [recordForEdit, setRecordForEdit] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [showChangeHotelModal, setShowChangeHotelModal] = useState(false);
    const [alternativeHotels, setAlternativeHotels] = useState([]);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [hotelToUpdate, setHotelToUpdate] = useState(null);

    useEffect(() => { if (packageId) { GetData(); } }, [packageId]);
    useEffect(() => { if (recordForEdit !== null) { setValues(recordForEdit); } }, [recordForEdit]);
    useEffect(() => {
        if (values.checkInDate && values.checkOutDate) {
            const checkIn = new Date(values.checkInDate);
            const checkOut = new Date(values.checkOutDate);
            if (checkOut > checkIn) {
                const diffTime = Math.abs(checkOut - checkIn);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                setValues(prev => ({ ...prev, nights: diffDays }));
            }
        }
    }, [values.checkInDate, values.checkOutDate]);

    const GetData = async () => {
        const res = await apiService.get(`${config.GETHOTELBYPACKAGEID}/${packageId}`);
        setLists(res.data);
    };

    const handleChangeHotelClick = async (hotelBooking) => {
        setHotelToUpdate(hotelBooking);
        setShowChangeHotelModal(true);
        setIsModalLoading(true);
        setAlternativeHotels([]);

        try {
            const placeInfo = hotelSearchData.find(cityData => 
                cityData.cityName.toLowerCase() === hotelBooking.city.toLowerCase()
            );

            if (!placeInfo || !placeInfo.id) {
                toast.error(`City code for "${hotelBooking.city}" could not be found.`);
                throw new Error("Missing cityCode");
            }
            
            const hotelSearchRequest = {
                companyId: user.cid, userId: user.id, city: placeInfo.cityName, cityCode: placeInfo.id.toString(), country: placeInfo.countryName, checkInDate: hotelBooking.checkInDate, checkOutDate: hotelBooking.checkOutDate, totalGuests: (packageDetails.adults || 1) + (packageDetails.children || 0),
                hotelRooms: [{
                    adults: packageDetails.adults || 1, kids: packageDetails.children || 0, kidsAge: []
                }],
                rating: [parseInt((packageDetails?.hotelCategory || "4 Star").charAt(0))],
                currency: "INR", nationality: "106",
            };
            
            const res = await apiService.post(config.SEARCHHOTELALTERNATIVES, hotelSearchRequest);
            console.log("Alternative hotels response:", res);

            // --- THE FINAL FIX ---
            // The hotel list is directly inside res.data, not res.data.data
            const hotelList = res.data?.commonHotelDetails;
            setAlternativeHotels(hotelList || []);

        } catch (error) {
            if (error.message !== "Missing cityCode") {
                 toast.error(error.response?.data?.message || "Could not load alternative hotels.");
            }
            setShowChangeHotelModal(false);
        } finally {
            setIsModalLoading(false);
        }
    };

    const handleSelectNewHotel = async (selectedHotel) => {
        if (!hotelToUpdate || !selectedHotel) {
            toast.error("An error occurred. Missing hotel information.");
            return;
        }
        setBtnLoading(true);
        try {
            const formData = {
                ...hotelToUpdate,
                hotelName: selectedHotel.hotelName,
                roomType: selectedHotel.commonHotelDefaultRoom?.roomType || hotelToUpdate.roomType,
                price: selectedHotel.totalSalePrice
            };
            await apiService.update(config.UPDATEPACKAGEHOTEL, formData);
            setShowChangeHotelModal(false);
            await GetData();
        } catch (error) {
            toast.error("Failed to update hotel.");
        } finally {
            setBtnLoading(false);
        }
    };
       const applyErrorClass = (field) =>
     field in errors && errors[field] === false ? " form-control-danger" : "";

    // ... All other handler functions (delete, edit, etc.) remain the same ...
    const handleOpenDeleteModal = (id) => { setItemToDelete(id); setShowDeleteModal(true); };
    const handleCloseDeleteModal = () => { setItemToDelete(null); setShowDeleteModal(false); };
    const handleDelete = async () => { if (itemToDelete) { await apiService.remove(config.DELETEPACKAGEHOTEL, itemToDelete); await GetData(); handleCloseDeleteModal(); } };
    const handleAddNewClick = () => { resetForm(); setIsFormVisible(true); };
    const showEditDetails = (data) => {
        const formattedData = { ...data, checkInDate: moment(data.checkInDate).format('YYYY-MM-DD'), checkOutDate: moment(data.checkOutDate).format('YYYY-MM-DD') };
        setRecordForEdit(formattedData);
        setValues(formattedData);
        setIsFormVisible(true);
    };
    const validate = () => {
        let temp = {};
        temp.hotelName = values.hotelName && values.hotelName.trim() !== "" ? true : false;
        temp.city = values.city && values.city.trim() !== "" ? true : false;
        temp.checkInDate = values.checkInDate !== "" ? true : false;
        temp.checkOutDate = values.checkOutDate !== "" ? true : false;
        temp.numberOfRooms = values.numberOfRooms > 0;
        
        setErrors(temp);
        return Object.values(temp).every((x) => x === true);
    };
    
    const handleSubmit = async (e) => { e.preventDefault(); if (validate()) { setBtnLoading(true); const formData = { ...values, packageId, userId: user?.id }; try { if (formData.packageHotelBookingId === "00000000-0000-0000-0000-000000000000") { await apiService.create(config.CREATEPACKAGEHOTEL, formData); } else { await apiService.update(config.UPDATEPACKAGEHOTEL, formData); } await GetData(); resetForm(); setIsFormVisible(false); } finally { setBtnLoading(false); } } };
    const handleCancel = () => { resetForm(); setIsFormVisible(false); };
    const handleInputChange = (e) => { const { name, value } = e.target; setValues({ ...values, [name]: value }); };
    const resetForm = () => { setValues(initialFieldValues); setRecordForEdit(null); setBtnLoading(false); setErrors({}); };

        return (
        <div className="PackageHotel-wrapper">
            <div className="card PackageHotel-card">
                <div className="card-body">
                    <div className="PackageHotel-header">
                        <h4 className="header-title">Hotel Bookings</h4>
                        {!packageDetails?.isLive && !isFormVisible && (
                            <button type="button" className="btn btn-primary" onClick={handleAddNewClick}><i className="ri-add-line me-1"></i> Add Hotel Stay</button>
                        )}
                    </div>
                    {isFormVisible && (
                        <div className="PackageHotel-form-container">
                            <h5>{values.packageHotelBookingId === "00000000-0000-0000-0000-000000000000" ? "Add" : "Edit"} Hotel</h5>
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-lg-3 mb-3"><label>Hotel Name</label><input type="text" name="hotelName" value={values.hotelName} onChange={handleInputChange} className={"form-control" + applyErrorClass("hotelName")}  placeholder="Grand Hyatt"/> {errors.hotelName === false && <div className="validationerror">Please enter hotel name</div>}</div>
                                    <div className="col-lg-3 mb-3"><label>City</label><input type="text" name="city" value={values.city} onChange={handleInputChange} className={"form-control" + applyErrorClass("city")}  placeholder="New York"/>{errors.city === false && <div className="validationerror">Please enter city</div>}</div>
                                    <div className="col-lg-3 mb-3"><label>Check-In</label><input type="date" name="checkInDate" value={values.checkInDate} onChange={handleInputChange} className={"form-control" + applyErrorClass("checkInDate")}  />{errors.checkInDate === false && <div className="validationerror">Please enter check-in date</div>}</div>
                                    <div className="col-lg-3 mb-3"><label>Check-Out</label><input type="date" name="checkOutDate" value={values.checkOutDate} onChange={handleInputChange} className={"form-control" + applyErrorClass("checkOutDate")} />{errors.checkOutDate === false && <div className="validationerror">Please enter check-out date</div>}</div>
                                    <div className="col-lg-3 mb-3"><label>Room Type</label><select name="roomType" value={values.roomType} onChange={handleInputChange} className="form-select"><option value="">Select</option><option>Standard</option><option>Deluxe</option><option>Suite</option></select></div>
                                    <div className="col-lg-3 mb-3"><label>No. of Rooms</label><input type="number" name="numberOfRooms" value={values.numberOfRooms} onChange={handleInputChange} className={"form-control" + applyErrorClass("numberOfRooms")} min="1"/></div>
                                    <div className="col-lg-3 mb-3"><label>Price</label><input type="number" name="price" value={values.price} onChange={handleInputChange} className="form-control" /></div>
                                </div>
                                <div className="d-flex justify-content-end gap-2"><button type="button" className="btn btn-light" onClick={handleCancel}>Cancel</button><button type="submit" className="btn btn-primary" disabled={btnLoading}>{btnLoading ? "Saving..." : "Submit"}</button></div>
                            </form>
                        </div>
                    )}
                    <div className="table-responsive mt-4">
                        <table className="table PackageHotel-table">
                            <thead>
                                <tr>
                                    <th>Hotel</th>
                                    <th>Check-In</th>
                                    <th>Check-Out</th>
                                    <th className="text-center">Nights</th>
                                    <th>Room Details</th>
                                    <th>Price</th>
                                    <th className="text-center">Actions</th></tr></thead>
                            <tbody>
                                {lists.length > 0 ? lists.map((v) => (
                                    <tr key={v.packageHotelBookingId}>
                                        <td><div className="fw-medium">{v.hotelName}</div><div className="location-text"><i className="ri-map-pin-2-fill me-1"></i>{v.city}</div></td>
                                        <td>{moment(v.checkInDate).format("ddd, DD MMM YYYY")}</td>
                                        <td>{moment(v.checkOutDate).format("ddd, DD MMM YYYY")}</td>
                                        <td className="text-center"><span className="PackageHotel-nights-badge">{v.nights}</span></td>
                                        <td><div>{v.roomType}</div><div className="rooms-text">Rooms: {v.numberOfRooms}</div></td>
                                        <td>{v.price}</td>
                                        
                                        <td className="text-center">
  <div className="d-flex gap-2 justify-content-center">

    {/* If package is live → hide Edit, show Change Hotel button */}
    {packageDetails?.isLive && (
      <button
        className="PackageHotel-action-btn change"
        title="Change Hotel"
        style={{
          backgroundColor: "#ffc107",
          color: "#fff",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer"
        }}
       onClick={() => handleChangeHotelClick(v)}
      >
        <i className="ri-swap-line" />
      </button>
    )}

    {/* If NOT live → show Edit */}
    {!packageDetails?.isLive && (
      <button className="PackageHotel-action-btn edit" onClick={() => showEditDetails(v)}>
        <i className="ri-pencil-fill"/>
      </button>
    )}

    {/* Delete should always appear */}
    <button className="PackageHotel-action-btn delete" onClick={() => handleOpenDeleteModal(v.packageHotelBookingId)}>
      <i className="ri-delete-bin-fill"/>
    </button>

  </div>
</td>

                                    </tr>
                                )) : (
                                    <tr><td colSpan="6"><div className="PackageHotel-empty-state"><i className="ri-hotel-line empty-icon"></i><h5>No Hotels Added Yet</h5></div></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
            <ChangeHotelModal show={showChangeHotelModal} onHide={() => setShowChangeHotelModal(false)} hotels={alternativeHotels} isLoading={isModalLoading} onSelect={handleSelectNewHotel} currentlySelectedHotel={hotelToUpdate}/>
        </div>
    );
};

export default PackageHotelBooking;